import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Button, Grid, Icon, NetworkLogo } from 'gu-ui-kit'
import { IS_APP_PRODUCT_ADSIMPLE } from 'utils/constants/product'
import Nav from 'components/Nav'
import Footer from 'components/Footer'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { APP_PRODUCT } from 'constants/app-config'
import { useDispatch, useSelector } from 'react-redux'
import DrawerFooter from 'components/Drawer/subcomponents/DrawerFooter'
import BalanceWidget from 'components/Drawer/subcomponents/BalanceWidget'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { SET_DRAWER_STATE } from 'modules/App/types'
import GridSpinner from 'components/UI/GridSpinner'
import defaultNavigationConfig from 'constants/navigation'
import { selectAppUser, selectAppDrawerState } from 'modules/App/store/selectors'
import findMatchedRoutes, { getLastMatchedRoute } from 'utils/findMatchedRoutes'
import styles from 'components/Drawer/drawer.module.scss'

const Drawer = ({
  children,
  className,
  navItems: navItemsProp,
  navLoading = false,
  collapsible = true,
  hasBalance = true,
  footer,
}) => {
  const breakpoints = useBreakpoint()
  const user = useSelector(selectAppUser) ?? {}
  const drawerState = useSelector(selectAppDrawerState)
  const staticNavItems = useMemo(() => defaultNavigationConfig({ user }), [user])
  const navItems = navItemsProp ?? staticNavItems
  const showNavSkeleton = navLoading && navItems.length === 0
  const pathGen = useGeneratePathI18n()

  const asideRef = useRef(null)
  const collapse = collapsible && drawerState.collapsed
  const dispatch = useDispatch()

  const matchedRoutes = findMatchedRoutes()

  const closeSidebar = useCallback(() => {
    dispatch({ type: SET_DRAWER_STATE, payload: { opened: false } })
  }, [dispatch])

  useEffect(() => {
    document.querySelector('body').classList.toggle('body_overflow_hidden', drawerState.opened)
    document.querySelector('body').classList.toggle('detached_drawer_shown', drawerState.opened)
  }, [drawerState])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!drawerState.opened) return

      if (asideRef.current) {
        const rect = asideRef.current.getBoundingClientRect()
        const { clientX, clientY } = e

        if (
          clientX < rect.left ||
          clientX > rect.right ||
          clientY < rect.top ||
          clientY > rect.bottom
        ) {
          closeSidebar()
        }
      }
    }

    const handleKeyDown = (e) => {
      if (!drawerState.opened) return
      if (e.key === 'Escape') {
        closeSidebar()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [breakpoints.xl, closeSidebar, drawerState.opened])

  return (
    <>
      <aside
        className={clsx(
          styles['drawer'],
          {
            [styles['drawer_opened']]: drawerState.opened,
            [styles['drawer_collapsed']]: collapse && breakpoints.xl,
            [styles['drawer_detached']]: !breakpoints.xl,
          },
          className,
        )}
      >
        <div
          className={clsx(styles['drawer-inner'], 'layout-tile')}
          ref={asideRef}
        >
          <Grid
            direction={'column'}
            flexGrow={1}
            className={styles['drawer-inner__grid']}
          >
            {breakpoints.xl && (
              <Grid
                direction={'row'}
                wrap={false}
                justifyContent={collapse ? 'center' : 'space-between'}
                alignItems={'center'}
                className={clsx('margin-bottom-24', styles['drawer-inner__grid_head'], {
                  [styles['drawer-inner__grid_head_collapsed']]: collapse,
                })}
              >
                <Link to={pathGen(ROUTES.SERVICES)}>
                  <NetworkLogo
                    name={APP_PRODUCT}
                    color={
                      IS_APP_PRODUCT_ADSIMPLE
                        ? collapse
                          ? 'Interface-GU-Primary'
                          : 'Label-Primary'
                        : 'Interface-Eleven'
                    }
                    type={collapse ? 'unlinked' : 'component'}
                    width={collapse ? 36 : 106}
                    height={36}
                  />
                </Link>

                {collapsible && (
                  <Button
                    className={clsx(styles['drawer-toggler'], {
                      [styles['drawer-toggler_collapsed']]: collapse,
                    })}
                    variant={'ghost'}
                    color={'Interface-Eighth'}
                    noLabel
                    size={'s'}
                    onClick={() =>
                      dispatch({ type: SET_DRAWER_STATE, payload: { collapsed: !collapse } })
                    }
                    startIcon={<Icon name={collapse ? 'arrow-next' : 'arrow-previous'} />}
                  />
                )}
              </Grid>
            )}

            {breakpoints.xl && hasBalance && (
              <Grid
                fluid
                alignContent={'center'}
                direction={'row'}
                justifyContent={'center'}
              >
                <BalanceWidget
                  className={clsx('margin-bottom-24', styles['drawer-inner__grid_balance'])}
                  collapse={collapse && breakpoints.xl}
                />
              </Grid>
            )}

            {showNavSkeleton ? (
              <GridSpinner className={styles['drawer-inner__grid_top']} />
            ) : (
              <Nav
                className={styles['drawer-inner__grid_top']}
                items={navItems}
                collapse={collapse && breakpoints.xl}
              />
            )}
            <Grid>
              <DrawerFooter collapse={collapse && breakpoints.xl} />
            </Grid>
          </Grid>
        </div>
      </aside>
      <main className={styles['content']}>
        <div
          className={clsx({
            ['layout-tile']: !getLastMatchedRoute(matchedRoutes)?.restyled,
          })}
        >
          {children}
        </div>
        {footer ?? <Footer />}
      </main>
    </>
  )
}

Drawer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.oneOf([null]),
  ]),
  className: PropTypes.string,
  navItems: PropTypes.array,
  navLoading: PropTypes.bool,
  collapsible: PropTypes.bool,
  hasBalance: PropTypes.bool,
  footer: PropTypes.node,
}

export default Drawer
